<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="modalTitle"
      size="large"
      :close-label="isViewMode ? t('Fechar') : t('Cancelar')"
      :save-label="modalSaveLabel"
      save-icon="pi pi-clone"
      :save-fn="handleSave"
      :save-disabled-fn="() => !formRef?.canSave"
      data-testid="plano-contas-modal"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <PlanoContasForm
        ref="formRef"
        :mode="mode"
        :plan="plan"
        @saved="onSaved"
      />
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import PlanoContasForm from './PlanoContasForm.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

type FormMode = 'create' | 'clone' | 'view';

interface Plan {
  franquia_id?: number | string;
  nome?: string;
  [key: string]: unknown;
}

interface OpenPayload {
  mode?: FormMode;
  plan?: Plan | null;
}

interface FormInstance {
  save: () => Promise<void>;
  canSave: boolean;
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const mode = ref<FormMode>('create');
const plan = ref<Plan | null>(null);
const formRef = ref<FormInstance | null>(null);

const isViewMode = computed<boolean>(() => mode.value === 'view');

const modalTitle = computed<string>(() => {
  if (mode.value === 'create') {
    return t('Novo Plano');
  }
  if (mode.value === 'view' && plan.value) {
    return t('Plano de Contas') + (plan.value.nome ? ` - ${plan.value.nome}` : '');
  }
  if (plan.value) {
    return t('Clonar Plano') + (plan.value.nome ? ` - ${plan.value.nome}` : '');
  }
  return t('Clonar Plano');
});

const modalSaveLabel = computed<string | undefined>(() => {
  if (mode.value === 'view') return undefined;
  if (mode.value === 'create') return t('Criar');
  return t('Clonar');
});

const handleSave = async (): Promise<void> => {
  if (formRef.value) {
    await formRef.value.save();
  }
};

const onSaved = (): void => {
  log('PlanoContasModal', 'onSaved', 'Plan saved, closing modal');
  isVisible.value = false;
};

const handleClose = (): void => {
  log('PlanoContasModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  mode.value = 'create';
  plan.value = null;
};

const handleOpen = (data: OpenPayload | undefined): void => {
  log('PlanoContasModal', 'handleOpen', 'Opening modal via eventBus', data);
  mode.value = data?.mode || 'create';
  plan.value = data?.plan || null;
  isVisible.value = true;
};

onMounted(() => {
  log('PlanoContasModal', 'onMounted', 'Modal mounted, listening for events');
  eventBus.on('modal.plano-contas.open', handleOpen);
});

onBeforeUnmount(() => {
  log('PlanoContasModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.plano-contas.open', handleOpen);
});
</script>

<style scoped>
/* Modal styles inherited from ui/Modal */
</style>
